/**
 * Populate product data for reservation
 * @param {*} product - The product object
 * @returns {Object|null} - The populated product data or null if product is not provided
 */
export function populateProduct(product) {
    if (!product) return null;
    return {
        _id: product._id,
        name: product.name,
        code: product.code,
        type: product.type,
        passengers: product.passengers,
        bags: product.bags,
        images: product.images,
        currency: product.currency,
        pricing: product.pricing,
        slug: product.slug,
        destination: product.destination,
        shortdescription: product.short_description,
        durationUnit: product.duration.unit,
        durationValue: product.duration.value,
        pricing_method: product.pricing_method,
        area: product.area,
        transfer: product.transfer,
        service: product.service,
        supplier: product.supplier,
        branch: product.branch,
        duration: product.duration,
        show_options: product.show_options
    };
}

/**
 * Populate agent data for reservation
 * @param {*} agent - The agent object
 * @returns {Object|null} - The populated agent data or null if agent is not provided
 */
export function populateAgent(agent) {
    if (!agent) return null;
    return {
        _id: agent._id,
        first_name: agent.first_name,
        last_name: agent.last_name,
        gender: agent.gender,
        email: agent.email,
        phone: agent.phone,
        other_contact_platform: agent.other_contact_platform,
        contact_info: agent.contact_info,
        type: agent.type,
        owner: agent.association._id,
        company_name: agent.association ? agent.association.company_name : "",
        mark_up: agent.association ? agent.association.mark_up : 0.68,
        type_markup: agent.association
            ? agent.association.type_markup
            : "markup",
        commission: agent.commission,
        commercialTerms: agent.type_charge,
        currency: agent.association
            ? agent.association.currency
            : { code: "USD", symbol: "$", label: "US Dollar" },
        credit: agent.association
            ? agent.association.credit
            : { status: false, limit: 0, available: 0 }
    };
}

/**
 * Populate customer data for reservation
 * @param {*} customer - The customer object
 * @returns {Object|null} - The populated customer data or null if customer is not provided
 */
export function populateCustomer(customer) {
    if (!customer) return null;
    return {
        _id: customer._id,
        first_name: customer.first_name,
        last_name: customer.last_name,
        gender: customer.gender,
        email: customer.email,
        phone: customer.phone,
        other_contact_platform: customer.other_contact_platform,
        contact_info: customer.contact_info,
        type: customer.type,
        company_name: customer.company_name,
        mark_up: customer.mark_up,
        type_markup: customer.type_markup,
        commission: customer.commission,
        commercialTerms: customer.type_charge,
        currency: customer.currency
            ? customer.currency
            : { code: "USD", symbol: "$", label: "US Dollar" },
        credit: customer.currency
            ? customer.credit
            : { status: false, limit: 0, available: 0 }
    };
}

/**
 * Populate driver data for reservation
 * @param {*} driver - The driver object
 * @returns {Object} - The populated driver data
 */
export function populateDriver(driver) {
    if (!driver) return null;
    return {
        person: {
            first_name: driver.person.first_name,
            last_name: driver.person.last_name,
            full_name: driver.person.full_name,
            gender: driver.person.gender,
            numbre_phone: driver.person.numbre_phone
        },
        profile: {
            image: driver.profile.image
        },
        relationship: {
            branch: driver.relationship.branch,
            auto_assign: driver.relationship.auto_assign
        },
        account: {
            email: driver.account.email
        }
    };
}

/**
 * Populate service data for reservation
 * @param {Object} reservation - The reservation object
 * @param {Object} vehicle - The vehicle object
 * @returns {Object|null} - The populated service data or null if service is not provided
 */
export function populateService(reservation, vehicle) {
    let { service, pricing, supplier } = reservation;
    if (!service) return null;
    return {
        _id: service._id,
        vehicle: {
            _id: vehicle._id,
            seats: vehicle.parameters.n_seats,
            suitcases: vehicle.parameters.n_suitcases,
            type_service: vehicle.parameters.type_category,
            category: vehicle.parameters.category,
            make: vehicle.parameters.make,
            make_label: vehicle.parameters.name,
            image: vehicle.information.image,
            isComposite: vehicle.parameters.isComposite
        },
        hours_advance: service.advancedReservation,
        preference: service.preference,
        currency: {
            code: service.currency_code,
            symbol: service.currency_symbol,
            label: service.currency_label
        },
        branchId: service.branch,
        supplierId: supplier ? supplier._id : null,
        base_price: pricing.base_price.last_amount,
        sale_price: pricing.sale_price.last_amount,
        basePriceComposition: pricing.base_price.composition,
        surcharges: reservation.surcharges,
        unit: supplier ? supplier.settings.measurementUnit : "km",
        distance_include: service.hours_disposition
            ? service.hours_disposition.km_include
            : 0,
        min_hours: service.hours_disposition
            ? service.hours_disposition.min_hours
            : 0,
        cancellationPolicy: service.cancellationPolicy
            ? service.cancellationPolicy.descriptions
            : [],
        keyDetails: service.keyDetails,
        termsConditions: service.termsConditions,
    };
}